// Combined auth experience for Mudo visitors + studios
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  CheckCircle2,
  KeyRound,
  Link2,
  Loader2,
  Lock,
  Mail,
  Sparkles,
  User,
} from "lucide-react";
import { supabase } from "@/supabaseClient";
import { useAuth } from "../../contexts/AuthContext";
import { APP_BASE_URL } from "@/lib/env";
import { splitFullName } from "@/utils/splitFullName";

const MIN_PASSWORD_LENGTH = 8;

function getPostAuthTarget() {
  // 1. Check if there's a next query parameter (deep link)
  const params = new URLSearchParams(window.location.search);
  const next = params.get('next');
  if (next && next.startsWith('/')) {
    return next;
  }

  // 2. Check sessionStorage for post-auth path
  try {
    const storedPath = sessionStorage.getItem('mudo_post_auth_path');
    if (storedPath) {
      sessionStorage.removeItem('mudo_post_auth_path'); // Clear after reading
      return storedPath;
    }
  } catch (err) {
    console.warn('[LiquidGlassAuth] Could not read sessionStorage:', err);
  }

  // 3. Check localStorage for selected world
  try {
    const world = localStorage.getItem('mudo_world');
    if (world) {
      return `/${world}/home`;
    }
  } catch (err) {
    console.warn('[LiquidGlassAuth] Could not read localStorage:', err);
  }

  // 4. Fallback to /home
  return '/home';
}

function buildRedirectUrl() {
  try {
    return (
      import.meta.env.VITE_SUPABASE_REDIRECT_URL ||
      `${(APP_BASE_URL || window.location.origin)}/auth/callback`
    );
  } catch {
    return undefined;
  }
}

function buildResetRedirectUrl() {
  try {
    return (
      import.meta.env.VITE_SUPABASE_RESET_REDIRECT_URL ||
      `${(APP_BASE_URL || window.location.origin)}/auth/reset`
    );
  } catch {
    return undefined;
  }
}

export default function LiquidGlassAuthPage() {
  const navigate = useNavigate();
  const { user, signIn } = useAuth() || {};

  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [studioName, setStudioName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const [magicStatus, setMagicStatus] = useState("");
  const [magicLoading, setMagicLoading] = useState(false);

  const [setPasswordOpen, setSetPasswordOpen] = useState(false);
  const [setPasswordEmail, setSetPasswordEmail] = useState("");
  const [setPasswordNew, setSetPasswordNew] = useState("");
  const [setPasswordConfirm, setSetPasswordConfirm] = useState("");
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  const [setPasswordStatus, setSetPasswordStatus] = useState("");
  const [setPasswordError, setSetPasswordError] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const redirectTo = useMemo(() => buildRedirectUrl(), []);
  const resetRedirectTo = useMemo(() => buildResetRedirectUrl(), []);

  useEffect(() => {
    if (setPasswordOpen && !setPasswordEmail && email) {
      setSetPasswordEmail(email);
    }
  }, [setPasswordOpen, setPasswordEmail, email]);

  const resetUiState = () => {
    setErrorMessage("");
    setStatusMessage("");
    setMagicStatus("");
  };

  const validatePassword = (pwd) => {
    if (!pwd || pwd.length < MIN_PASSWORD_LENGTH) {
      return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    return "";
  };

  const handleLogin = async () => {
    if (!email.trim() || !password) {
      setErrorMessage("Please enter your email and password.");
      return;
    }
    const pwdError = validatePassword(password);
    if (pwdError) {
      setErrorMessage(pwdError);
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      console.log("[LiquidGlassAuth] Attempting login...");
      
      if (!signIn) {
        throw new Error("Auth context not available");
      }
      
      await signIn({ 
        email: email.trim(), 
        password 
      });
      
      console.log("[LiquidGlassAuth] Login successful, navigating...");
      const target = getPostAuthTarget();
      navigate(target);
    } catch (err) {
      console.error("[LiquidGlassAuth] Login error", err);

      const msg = String(err?.message || "");
      if (msg.toLowerCase().includes("timeout") || msg.toLowerCase().includes("network error")) {
        setErrorMessage("Connection timeout. Please check your internet connection and try again.");
      } else {
        setErrorMessage(
          "Incorrect email or password. If you used a magic link before, please use 'Set password' to create a password for your account."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!email.trim() || !password) {
      setErrorMessage("Please enter your email and password.");
      return;
    }
    const pwdError = validatePassword(password);
    if (pwdError) {
      setErrorMessage(pwdError);
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setStatusMessage("");

    try {
      // Split full name into first and last name for user_profiles
      const { firstName, lastName } = splitFullName(fullName);

      // Call supabase.auth.signUp with user metadata that aligns with user_profiles schema.
      // user_profiles rows are created server-side via database trigger on auth.users INSERT,
      // so we just pass metadata and let the trigger handle profile creation.
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: redirectTo,
          data: {
            first_name: firstName || undefined,
            last_name: lastName || undefined,
            studio_name: studioName || undefined,
            location_country: undefined,
            location_city: undefined,
            role: undefined,
            onboarding_status: "profile_pending",
          },
        },
      });

      if (error) throw error;

      if (!data?.user?.id) {
        throw new Error("Signup failed: No user ID returned from Supabase");
      }

      if (import.meta.env.DEV) {
        console.log("[LiquidGlassAuth] Signup successful", { 
          userId: data?.user?.id, 
          email: data?.user?.email,
          hasSession: !!data?.session,
          userMetadata: data?.user?.user_metadata
        });
      }

      // If session is created, user is confirmed - login immediately
      if (data?.session) {
        setStatusMessage("Account created! Redirecting to your dashboard...");
        const target = getPostAuthTarget();
        navigate(target);
      } else {
        // Email confirmation is enabled - session is null until email is verified
        // For development: wait a moment for trigger to create user_profiles, then auto-login
        if (import.meta.env.DEV) {
          console.warn("[LiquidGlassAuth] Email confirmation pending - account created with userId:", data?.user?.id);
          
          // Wait a moment for database triggers to complete
          await new Promise(r => setTimeout(r, 800));
          
          // Attempt to sign in with the credentials
          try {
            const loginResult = await signIn({ email: email.trim(), password });
            if (loginResult) {
              setStatusMessage("Account created! Logging you in...");
              const target = getPostAuthTarget();
              navigate(target);
            } else {
              console.warn("[LiquidGlassAuth] Login returned falsy result");
              setStatusMessage("Account created! Please verify your email to continue.");
            }
          } catch (loginErr) {
            console.error("[LiquidGlassAuth] Auto-login failed:", loginErr?.message);
            setStatusMessage("Account created! Please check your email to confirm your account.");
          }
        } else {
          setStatusMessage("Account created! Please check your email to confirm your account.");
        }
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error("[LiquidGlassAuth] Signup error", err);
      }
      setErrorMessage(
        err?.message || "We could not create your account. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordResetRequest = async (e) => {
    e?.preventDefault?.();
    const targetEmail = (resetEmail || email || "").trim();
    if (!targetEmail) {
      setResetError("Please enter your email address.");
      setResetStatus("");
      return;
    }
    setResetLoading(true);
    setResetError("");
    setResetStatus("");
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(targetEmail, {
        redirectTo: resetRedirectTo,
      });
      if (error) throw error;
      setResetStatus("If this email is registered, we’ve sent a password reset link.");
    } catch (err) {
      console.error("[Auth] resetPasswordForEmail error", err);
      setResetError("Could not send reset link right now. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email.trim()) {
      setErrorMessage("Please enter your email to receive a magic link.");
      return;
    }
    setMagicLoading(true);
    setMagicStatus("");
    setErrorMessage("");

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: { emailRedirectTo: redirectTo },
      });
      if (error) throw error;
      setMagicStatus("We’ve sent you a magic link. Check your inbox.");
    } catch (err) {
      console.error("[Auth] magic link error", err);
      setErrorMessage(
        err?.message || "Could not send magic link. Please try again."
      );
    } finally {
      setMagicLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setSetPasswordError("");
    setSetPasswordStatus("");

    const targetEmail = (setPasswordEmail || email).trim();
    if (!targetEmail) {
      setSetPasswordError("Please enter your email.");
      return;
    }
    if (setPasswordNew !== setPasswordConfirm) {
      setSetPasswordError("Passwords do not match.");
      return;
    }
    const pwdError = validatePassword(setPasswordNew);
    if (pwdError) {
      setSetPasswordError(pwdError);
      return;
    }

    setSetPasswordLoading(true);
    try {
      if (user) {
        const { error } = await supabase.auth.updateUser({
          password: setPasswordNew,
        });
        if (error) throw error;
        setSetPasswordStatus(
          "Password updated. You can now log in with email + password next time."
        );
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(
          targetEmail,
          {
            redirectTo,
          }
        );
        if (error) throw error;
        setSetPasswordStatus(
          "Check your email to set a password. Use the magic link email if prompted."
        );
      }
    } catch (err) {
      console.error("[Auth] set password error", err);
      setSetPasswordError(
        err?.message || "Could not update password right now. Please try again."
      );
    } finally {
      setSetPasswordLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    resetUiState();
    if (mode === "login") {
      handleLogin();
    } else {
      handleSignup();
    }
  };

  const headline =
    mode === "login" ? "Welcome back to Mudo" : "Create your Mudo account";

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-900 via-[#1a0f0b] to-neutral-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_20%_20%,#d26b36,transparent_25%),radial-gradient(circle_at_80%_0,#5df0c0,transparent_20%),radial-gradient(circle_at_50%_80%,#2dd4bf,transparent_18%)]" />
      <div className="relative max-w-5xl mx-auto px-4 pb-16 pt-10">
        <button
          onClick={() => navigate("/start")}
          className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-xl shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2 text-amber-300 mb-3">
                <Sparkles className="w-4 h-4" />
                <span className="text-xs uppercase tracking-[0.2em]">
                  Liquid glass auth
                </span>
              </div>
              <h1 className="text-3xl font-bold mb-3">{headline}</h1>
              <p className="text-white/70 text-sm mb-8 max-w-xl">
                Use your email and password to access the studio and visitor
                tools. You can still request a magic link if that’s your thing.
              </p>

              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => {
                    setMode("login");
                    resetUiState();
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                    mode === "login"
                      ? "bg-amber-500/20 border-amber-400 text-white"
                      : "bg-white/5 border-white/10 text-white/70 hover:text-white"
                  }`}
                >
                  Login
                </button>
                <button
                  onClick={() => {
                    setMode("signup");
                    resetUiState();
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                    mode === "signup"
                      ? "bg-emerald-500/20 border-emerald-400 text-white"
                      : "bg-white/5 border-white/10 text-white/70 hover:text-white"
                  }`}
                >
                  Create account
                </button>
              </div>

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <label className="text-sm text-white/70">Email</label>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 focus-within:border-amber-400 focus-within:bg-white/10">
                    <Mail className="w-4 h-4 text-white/60" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-white/40"
                      required
                    />
                  </div>
                </div>

                {mode === "signup" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="text-sm text-white/70">Full name</label>
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 focus-within:border-emerald-400 focus-within:bg-white/10">
                        <User className="w-4 h-4 text-white/60" />
                        <input
                          type="text"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          placeholder="Alex Potter"
                          className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-white/40"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-white/70">
                        Studio name
                      </label>
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 focus-within:border-emerald-400 focus-within:bg-white/10">
                        <Building2 className="w-4 h-4 text-white/60" />
                        <input
                          type="text"
                          value={studioName}
                          onChange={(e) => setStudioName(e.target.value)}
                          placeholder="Banksia Ceramics"
                          className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-white/40"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm text-white/70">Password</label>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 focus-within:border-amber-400 focus-within:bg-white/10">
                    <Lock className="w-4 h-4 text-white/60" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-white/40"
                      minLength={MIN_PASSWORD_LENGTH}
                      required
                    />
                  </div>
                  <p className="text-xs text-white/50">
                    Minimum {MIN_PASSWORD_LENGTH} characters for better security.
                  </p>
                  {mode === "login" && (
                    <div className="text-xs text-amber-200">
                      <button
                        type="button"
                        className="underline underline-offset-2 hover:text-white"
                        onClick={() => {
                          setResetOpen(true);
                          setResetEmail(email);
                          setResetStatus("");
                          setResetError("");
                        }}
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}
                </div>

                {mode === "signup" && (
                  <div className="space-y-2">
                    <label className="text-sm text-white/70">
                      Confirm password
                    </label>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 focus-within:border-emerald-400 focus-within:bg-white/10">
                      <KeyRound className="w-4 h-4 text-white/60" />
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Repeat password"
                        className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-white/40"
                        minLength={MIN_PASSWORD_LENGTH}
                        required
                      />
                    </div>
                  </div>
                )}

                {errorMessage && (
                  <div className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errorMessage}</span>
                  </div>
                )}
                {statusMessage && (
                  <div className="flex items-center gap-2 text-sm text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>{statusMessage}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-emerald-500 text-black font-semibold shadow-lg shadow-amber-500/30 disabled:opacity-60 disabled:cursor-not-allowed transition-transform hover:scale-[1.01]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {mode === "login" ? "Signing in..." : "Creating account..."}
                    </>
                  ) : (
                    <>{mode === "login" ? "Login" : "Create account"}</>
                  )}
                </button>
              </form>

              <div className="mt-6 border-t border-white/10 pt-4 space-y-3">
                <div className="flex items-center justify-between text-sm text-white/70">
                  <span>Prefer passwordless?</span>
                  <button
                    onClick={handleMagicLink}
                    disabled={magicLoading}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 hover:bg-white/10 transition-colors disabled:opacity-60"
                  >
                    {magicLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Link2 className="w-4 h-4" />
                    )}
                    Send me a magic link
                  </button>
                </div>
                {magicStatus && (
                  <div className="flex items-center gap-2 text-sm text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>{magicStatus}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 text-sm text-white/70">
                <button
                  className="underline-offset-4 hover:underline text-amber-200"
                  onClick={() => setSetPasswordOpen((prev) => !prev)}
                  type="button"
                >
                  Used a magic link before? Set your password
                </button>
              </div>

              {setPasswordOpen && (
                <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
                  <p className="text-sm text-white/80">
                    {user
                      ? "Update your password now so you can log in with email + password next time."
                      : "If you originally signed in via magic link, request a reset email to set a password."}
                  </p>

                  <form className="space-y-3" onSubmit={handleSetPassword}>
                    <div className="space-y-2">
                      <label className="text-xs text-white/60">Email</label>
                      <input
                        type="email"
                        value={setPasswordEmail || email}
                        onChange={(e) => setSetPasswordEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="text-xs text-white/60">
                          {user ? "New password" : "Password for your account"}
                        </label>
                        <input
                          type="password"
                          value={setPasswordNew}
                          onChange={(e) => setSetPasswordNew(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                          minLength={MIN_PASSWORD_LENGTH}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs text-white/60">
                          Confirm password
                        </label>
                        <input
                          type="password"
                          value={setPasswordConfirm}
                          onChange={(e) => setSetPasswordConfirm(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                          minLength={MIN_PASSWORD_LENGTH}
                          required
                        />
                      </div>
                    </div>

                    {setPasswordError && (
                      <div className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                        <AlertCircle className="w-4 h-4" />
                        <span>{setPasswordError}</span>
                      </div>
                    )}
                    {setPasswordStatus && (
                      <div className="flex items-center gap-2 text-sm text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>{setPasswordStatus}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-end">
                      <button
                        type="submit"
                        disabled={setPasswordLoading}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15 transition-colors disabled:opacity-60"
                      >
                        {setPasswordLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Working...
                          </>
                        ) : user ? (
                          "Update password"
                        ) : (
                          "Send reset email"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {resetOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
                  <div className="w-full max-w-md rounded-2xl border border-white/15 bg-white/5 backdrop-blur-lg p-5 shadow-2xl space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-white">Reset password</h3>
                      <button
                        type="button"
                        className="text-sm text-white/60 hover:text-white"
                        onClick={() => setResetOpen(false)}
                      >
                        Close
                      </button>
                    </div>
                    <p className="text-sm text-white/70">
                      If this email is registered, we’ll send a password reset link.
                    </p>
                    <form onSubmit={handlePasswordResetRequest} className="space-y-3">
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                        required
                      />
                      {resetError && (
                        <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
                          {resetError}
                        </div>
                      )}
                      {resetStatus && (
                        <div className="text-sm text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2">
                          {resetStatus}
                        </div>
                      )}
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-sm text-white hover:bg-white/15"
                          onClick={() => setResetOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={resetLoading}
                          className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-emerald-500 text-black text-sm font-semibold shadow-lg disabled:opacity-60"
                        >
                          {resetLoading ? "Sending..." : "Send reset link"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-black/20 border border-white/10 rounded-2xl p-8 backdrop-blur-lg shadow-2xl flex flex-col justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-100 text-xs font-semibold">
                <Sparkles className="w-3 h-3" />
                Burnt Banksia energy
              </div>
              <h2 className="text-2xl font-semibold">Why this update?</h2>
              <p className="text-white/70 text-sm leading-relaxed">
                We now support classic email + password alongside magic links.
                Set a password once and keep using magic links whenever you
                like.
              </p>
              <div className="space-y-3">
                <FeatureRow
                  icon={<CheckCircle2 className="w-4 h-4 text-emerald-300" />}
                  title="Password or magic link"
                >
                  Choose whichever flow works best for you.
                </FeatureRow>
                <FeatureRow
                  icon={<CheckCircle2 className="w-4 h-4 text-emerald-300" />}
                  title="Profiles stay in sync"
                >
                  We immediately create or update your profile and studio rows
                  after sign-up.
                </FeatureRow>
                <FeatureRow
                  icon={<KeyRound className="w-4 h-4 text-amber-200" />}
                  title="Set password for old magic-link accounts"
                >
                  Give your existing magic-link account a password without
                  losing data.
                </FeatureRow>
              </div>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-sm text-white/70">
              <p className="font-semibold text-white mb-2">Need help?</p>
              <p>
                If you don’t see the reset email, check spam or resend. For
                stubborn logins, sign in with a magic link once, then use “Set
                your password”.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureRow({ icon, title, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-1">{icon}</div>
      <div>
        <p className="text-white font-semibold text-sm">{title}</p>
        <p className="text-white/70 text-sm">{children}</p>
      </div>
    </div>
  );
}
